import { useId, useMemo, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../button';
import styles from './bottom-sheet.module.css';
import {
  CLOSE_BUTTON_TESTID,
  DEFAULT_INITIAL_SNAP_INDEX,
  DEFAULT_MAX_HEIGHT_PCT,
  DEFAULT_MIN_HEIGHT_PX,
  DEFAULT_SNAP_POINTS,
  HANDLE_TESTID
} from './bottom-sheet.constants';
import { useBottomSheetDrag, useFocusTrap, useViewportHeight } from './bottom-sheet.hooks';
import type { BottomSheetProps } from './bottom-sheet.types';
import { canStartFullBleedHeaderDrag, isButtonPointerTarget, resolveViewportHeight } from './bottom-sheet.utils';

export { DEFAULT_INITIAL_SNAP_INDEX, DEFAULT_MAX_HEIGHT_PCT, DEFAULT_MIN_HEIGHT_PX, DEFAULT_SNAP_POINTS } from './bottom-sheet.constants';
export type { BottomSheetProps } from './bottom-sheet.types';

const cx = (...classes: Array<string | false | undefined>): string => classes.filter(Boolean).join(' ');

const CloseIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true' focusable='false'>
    <path
      d='M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z'
      fill='currentColor'
    />
  </svg>
);

export const BottomSheet = ({
  open,
  onClose,
  header,
  body,
  modal = true,
  closeOnBackdropClick = true,
  ariaLabel,
  panelClassName,
  showCloseButton = true,
  closeButtonAriaLabel = 'Close',
  hideOnDesktop = false,
  fullBleed = false,
  snapPoints = DEFAULT_SNAP_POINTS,
  collapsedHeightPx,
  initialSnap = DEFAULT_INITIAL_SNAP_INDEX,
  maxHeightPct = DEFAULT_MAX_HEIGHT_PCT,
  minHeightPx = DEFAULT_MIN_HEIGHT_PX,
  onResize,
  onSnapChange
}: BottomSheetProps) => {
  const panelRef = useRef<HTMLDialogElement>(null);
  const headerId = useId();
  const viewportH = useViewportHeight();

  const effectiveSnapPoints = useMemo(() => {
    if (collapsedHeightPx === undefined || viewportH <= 0) return snapPoints;
    const [, ...rest] = snapPoints;
    // Clamp so the fixed first snap never overtakes the next fractional snap or the max-height ceiling.
    // The drag hook treats snapPoints[0] as the lowest snap, so the array must stay ascending — on short
    // viewports an unclamped collapsedHeightPx/viewportH could exceed rest[0] (or maxHeightPct) and break it.
    const nextSnap = rest[0] ?? maxHeightPct;
    const collapsedPct = Math.min(collapsedHeightPx / viewportH, nextSnap, maxHeightPct);
    return [collapsedPct, ...rest];
  }, [collapsedHeightPx, viewportH, snapPoints, maxHeightPct]);

  const { heightPx, isDragging, handlePointerDown, handlePointerMove, handlePointerUp, handleKeyDown } = useBottomSheetDrag({
    open,
    viewportH,
    snapPoints: effectiveSnapPoints,
    initialSnap,
    maxHeightPct,
    minHeightPx,
    onClose,
    onResize,
    onSnapChange
  });

  useFocusTrap({ open: open && modal, panelRef, onClose });

  if (!open) return null;
  // Vite SPA today, but createPortal still needs document.body. Bail safely under any non-DOM consumer.
  if (typeof document === 'undefined') return null;

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) onClose();
  };

  const handleBodyPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!fullBleed) return;
    if (!canStartFullBleedHeaderDrag(event.target, `.${styles.bodyFlush} > header`)) return;

    handlePointerDown(event);
  };

  const handleBodyPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isButtonPointerTarget(event.target)) return;
    handlePointerMove(event);
  };

  const effectiveVh = Math.max(1, resolveViewportHeight(viewportH));
  // Advertise the actually-reachable range — releasing below `minHeightPx` closes the sheet, and `maxHeightPct` is the ceiling.
  const ariaValueMin = Math.round((minHeightPx / effectiveVh) * 100);
  const ariaValueMax = Math.round(maxHeightPct * 100);
  const ariaValueNow = Math.round((heightPx / effectiveVh) * 100);

  const panelStyle: CSSProperties & Record<`--${string}`, string> = {
    height: `${heightPx}px`,
    '--sheet-height': `${heightPx}px`,
    maxHeight: `${maxHeightPct * 100}dvh`
  };

  const hasHeader = header !== undefined;
  // Prefer an explicit ariaLabel; otherwise point aria-labelledby at the rendered header so the dialog always has an accessible name.
  const ariaLabelledBy = ariaLabel === undefined && hasHeader ? headerId : undefined;

  return createPortal(
    <div className={cx(styles.root, !modal && styles.nonModal, hideOnDesktop && styles.hideOnDesktop)} data-testid='bottom-sheet-root'>
      <button
        type='button'
        hidden={!modal}
        className={styles.backdrop}
        data-testid='bottom-sheet-backdrop'
        aria-label='Close sheet'
        tabIndex={-1}
        onClick={handleBackdropClick}
      />
      <dialog
        ref={panelRef}
        open
        className={cx(styles.panel, panelClassName, isDragging && styles.dragging, fullBleed && styles.panelClipped)}
        style={panelStyle}
        aria-modal={modal}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        onKeyDown={event => {
          if (!modal && event.key === 'Escape' && !event.defaultPrevented) onClose();
        }}
        data-testid='bottom-sheet-panel'>
        <div
          className={cx(styles.handleHit, fullBleed && styles.handleHitFloating)}
          data-testid={HANDLE_TESTID}
          role='slider'
          aria-orientation='vertical'
          aria-label='Resize sheet'
          aria-valuemin={ariaValueMin}
          aria-valuemax={ariaValueMax}
          aria-valuenow={ariaValueNow}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}>
          <div className={styles.handle} />
        </div>
        {showCloseButton && (
          <Button
            type='text'
            className={styles.closeButton}
            data-testid={CLOSE_BUTTON_TESTID}
            aria-label={closeButtonAriaLabel}
            icon={<CloseIcon />}
            onClick={onClose}
          />
        )}
        {hasHeader && (
          <header id={headerId} className={styles.header}>
            {header}
          </header>
        )}
        <div
          className={cx(styles.body, fullBleed && styles.bodyFlush)}
          onPointerDown={handleBodyPointerDown}
          onPointerMove={handleBodyPointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}>
          {body}
        </div>
      </dialog>
    </div>,
    document.body
  );
};
